import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { 
  Mail, 
  Phone, 
  MapPin, 
  ArrowRight, 
  Send, 
  CheckCircle 
} from 'lucide-react';
import { solutions } from '../../../data/solutions';

export const FooterSection: React.FC = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setEmail('');
    }
  };

  return (
    <footer className="relative bg-[#050608] text-zinc-400 border-t border-white/[0.08] overflow-hidden" role="contentinfo">
      {/* Top Banner CTA */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-8 pt-20 pb-16">
        <div className="relative rounded-3xl p-8 sm:p-12 md:p-16 overflow-hidden bg-gradient-to-b from-[#12141F] to-[#0A0C12] border border-white/[0.1] shadow-2xl">
          {/* Subtle Glow Background */}
          <div 
            className="absolute top-0 right-0 w-[450px] h-[450px] bg-[#ff5500]/[0.06] rounded-full blur-[120px] pointer-events-none" 
            aria-hidden="true" 
          />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <span className="font-mono text-xs uppercase tracking-widest text-[#ff5500] mb-3 block">
                Próximo Passo
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight mb-4">
                Pronto para digitalizar e escalar a sua empresa?
              </h2>
              <p className="text-zinc-300 text-base sm:text-lg max-w-2xl leading-relaxed">
                Quer você precise de um software exclusivo, integração de sistemas legados ou infraestrutura robusta em Angola, a equipe Hiperbite está pronta para executar.
              </p>
            </div>

            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-4">
              <NavLink
                to="/contactos"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full font-semibold text-black bg-[#ff5500] hover:bg-[#f15c12] shadow-[0_0_30px_rgba(0,245,255,0.35)] hover:shadow-[0_0_40px_rgba(0,245,255,0.5)] transition-all duration-300 hover:scale-[1.02] text-center"
              >
                <span>Fale com um Especialista</span>
                <ArrowRight className="w-4 h-4" />
              </NavLink>

              <a
                href="tel:+244935140392"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-mono text-sm text-zinc-300 bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] hover:text-white transition-all text-center"
              >
                <Phone className="w-4 h-4 text-[#ff5500]" />
                <span>+244 935 140 392</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Links & Newsletter */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-8 py-16 border-t border-white/[0.06]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
          
          {/* Brand & Newsletter (Span 5) */}
          <div className="lg:col-span-5 space-y-6">
            <NavLink to="/" className="flex items-center gap-3 inline-block">
              <div className="w-16 h-16 p-0.5 flex items-center justify-center">
                
                  <img src="/assets/images/logo/logo_orange.svg" alt="Hiperbite Ícone" className="w-full h-full object-contain" />
                
              </div>
              <span className="font-heading font-bold text-xl tracking-tight text-white">
                Hiperbite <span className="text-[#ff5500]">.ao</span>
              </span>
            </NavLink>

            <p className="text-zinc-400 text-sm leading-relaxed max-w-sm">
              Empresa angolana de tecnologia focada em soluções inovadoras, desenvolvimento sob medida e consultoria estratégica em TI.
            </p>

            {/* Newsletter */}
            <div className="pt-2">
              <p className="text-xs font-mono uppercase tracking-wider text-zinc-300 mb-3">
                Insights de Engenharia & Notícias Técnicas
              </p>
              {subscribed ? (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-mono">
                  <CheckCircle className="w-4 h-4" />
                  <span>Inscrição confirmada com sucesso!</span>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex items-center gap-2">
                  <div className="relative flex-grow">
                    <input
                      type="email"
                      required
                      placeholder="Seu e-mail corporativo"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.1] text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#ff5500] focus:ring-1 focus:ring-[#ff5500] transition-all"
                      aria-label="Endereço de e-mail para newsletter"
                    />
                  </div>
                  <button
                    type="submit"
                    className="p-3 rounded-xl bg-[#ff5500] text-black hover:bg-[#f15c12] transition-colors focus:outline-none focus:ring-2 focus:ring-[#ff5500]"
                    aria-label="Subscrever Newsletter"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Navigation Links (Span 3) */}
          <div className="lg:col-span-3">
            <h3 className="font-heading font-semibold text-white text-base mb-4 tracking-wide">
              Soluções
            </h3>
            <nav className="flex flex-col space-y-2.5 text-sm">
              {solutions.map((solution) => (
                <NavLink key={solution.slug} to={`/solucoes/${solution.slug}`} className="inline-block transition-all hover:translate-x-1 hover:text-white">
                  {solution.name}
                </NavLink>
              ))}
            </nav>
          </div>

          {/* Contact Details (Span 4) */}
          <div className="lg:col-span-4">
            <h3 className="font-heading font-semibold text-white text-base mb-4 tracking-wide">
              Sede & Atendimento
            </h3>
            <address className="not-italic space-y-3.5 text-sm">
              <div className="flex items-start gap-3 text-zinc-300">
                <MapPin className="w-4 h-4 text-[#ff5500] mt-1 shrink-0" />
                <span>Vila Pacífica I12-1, APT 1101, Luanda — Angola</span>
              </div>
              <div className="flex items-center gap-3 text-zinc-300">
                <Mail className="w-4 h-4 text-[#ff5500] shrink-0" />
                <a href="mailto:geral@hiperbite.ao" className="hover:text-white transition-colors">
                  geral@hiperbite.ao
                </a>
              </div>
              <div className="flex items-center gap-3 text-zinc-300">
                <Phone className="w-4 h-4 text-[#ff5500] shrink-0" />
                <a href="tel:+244935140392" className="hover:text-white transition-colors">
                  +244 935 140 392
                </a>
              </div>
            </address>

            {/* Social Icons */}
            <div className="flex items-center gap-3 mt-6">
              <a
                href="https://web.facebook.com/hipertite"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/[0.08] flex items-center justify-center text-zinc-300 hover:text-white hover:border-[#ff5500]/40 hover:bg-[#ff5500]/10 transition-all"
                aria-label="Hiperbite no Facebook"
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/hiperbite/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/[0.08] flex items-center justify-center text-zinc-300 hover:text-white hover:border-[#ff5500]/40 hover:bg-[#ff5500]/10 transition-all"
                aria-label="Hiperbite no Instagram"
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/hiperbite"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/[0.08] flex items-center justify-center text-zinc-300 hover:text-white hover:border-[#ff5500]/40 hover:bg-[#ff5500]/10 transition-all"
                aria-label="Hiperbite no LinkedIn"
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
            </div>
          </div>

        </div>
      </div>

      {/* Copyright Sub-bar */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-8 py-6 border-t border-white/[0.04] flex flex-col sm:flex-row items-center justify-between text-xs text-zinc-500 font-mono gap-4">
        <div>
          © {new Date().getFullYear()} Hiperbite.ao — Todos os direitos reservados.
        </div>
        <div className="flex items-center gap-6">
          <span>Luanda, Angola</span>
          <span>•</span>
          <span className="text-[#ff5500]">High-Performance Web Architecture</span>
        </div>
      </div>
    </footer>
  );
};
